import { useTranslation } from 'react-i18next';
import type { HomeworkWeek } from './types';

/**
 * Same thresholds the homework ring uses today, applied to one week at a time:
 * green from 25% of that week's exercises, orange from 10%, red below. A week
 * with nothing done is drawn hollow so "little" and "nothing" read differently.
 */
export function homeworkWeekColor(pct: number): string {
  if (pct >= 25) return '#4aba6a';
  if (pct >= 10) return '#f0a030';
  return '#e24b4a';
}

function formatWeek(weekStart: string, locale: string): string {
  const date = new Date(`${weekStart}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return weekStart;
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

/**
 * The homework stat for reports that carry per-week data: a headline with the
 * number of weeks the student did any homework, over two rows of circles (one
 * per class week, oldest first) coloured by that week's own completion.
 */
export function HomeworkWeeks({ weeks }: { weeks: HomeworkWeek[] }) {
  const { t, i18n } = useTranslation('admin');
  const activeWeeks = weeks.filter((week) => week.submittedExercises > 0).length;
  const columns = Math.max(1, Math.ceil(weeks.length / 2));

  return (
    <div className="flex w-[200px] flex-col items-center gap-3 sm:w-[220px]">
      <div className="text-center leading-tight">
        <div className="text-2xl font-semibold tabular-nums text-gray-900">
          {t('studentFeedback.statHomeworkWeeks', { done: activeWeeks, total: weeks.length })}
        </div>
        <div className="text-xs font-medium text-gray-900 sm:text-[13px]">{t('studentFeedback.statHomeworkWeeksLabel')}</div>
      </div>
      <ol
        className="m-0 grid w-full list-none gap-1.5 p-0"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        aria-label={t('studentFeedback.statHomeworkWeeksLabel')}
      >
        {weeks.map((week) => {
          const color = homeworkWeekColor(week.percentage);
          const empty = week.submittedExercises === 0;
          const title = t('studentFeedback.homeworkWeekTooltip', {
            date: formatWeek(week.weekStart, i18n.language),
            percentage: week.percentage,
          });
          return (
            <li
              key={week.weekStart}
              title={title}
              aria-label={title}
              data-testid="homework-week"
              data-empty={empty ? 'true' : 'false'}
              className="aspect-square w-full rounded-full border-2 justify-self-center"
              style={{ maxWidth: 16, borderColor: color, backgroundColor: empty ? "transparent" : color }}
            />
          );
        })}
      </ol>
    </div>
  );
}
